export const vehicleSlotId = 'cm-tab-vehicle-widget';
export const wheelSlotId = 'cm-tab-wheel-widget';
export const tireSlotId = 'cm-tab-tire-widget';

const HOME_TABS_MOUNT_SELECTOR = '#cm_ymm';
const SEARCH_TABS_MOUNT_ID = 'cm-tabs-search';
const WHEELS_COLLECTION_URL = '/wheels/';
const TIRES_COLLECTION_URL = '/tires/';
const SHARED_WHEEL_TIRE_FIELD = 'wheel_tire_diameter';
const VEHICLE_STORAGE_KEY = 'cm_variant-model';

const TAB = {
  vehicle: {
    key: 'vehicle',
    label: 'Search By Vehicle',
    panelId: 'cm-tab-vehicle',
    slotId: vehicleSlotId,
  },
  wheel: {
    key: 'wheels',
    label: 'Custom Search',
    panelId: 'cm-tab-wheel',
    slotId: wheelSlotId,
  },
  tire: {
    key: 'tires',
    label: 'Search By Size',
    panelId: 'cm-tab-tire',
    slotId: tireSlotId,
  },
};

let currentOptions = null;

function getSelection() {
  return window.Convermax.getSearchRequest?.()?.selection ?? [];
}

function isWheelsPage() {
  return window.location.pathname === WHEELS_COLLECTION_URL;
}

function isTiresPage() {
  return window.location.pathname === TIRES_COLLECTION_URL;
}

function isVehicleSelected() {
  return window.Convermax.isVehicleSelected?.() ?? hasStoredVehicleSelection();
}

function hasStoredVehicleSelection() {
  try {
    return !!window.localStorage.getItem(VEHICLE_STORAGE_KEY);
  } catch {
    return false;
  }
}

function hasHomeTabsMount() {
  return !!window.document.querySelector(HOME_TABS_MOUNT_SELECTOR);
}

function shouldRenderSearchTabs() {
  return !hasHomeTabsMount() && !!currentOptions?.shouldRenderSearchTabs;
}

function hasFieldInUrl(fieldName) {
  return window.location.href.includes(fieldName);
}

function hasFieldInSelection(selection, fieldName) {
  return selection.some((item) => item.field === fieldName);
}

function hasAnyField(fields, selection) {
  return fields.some((field) => hasFieldInUrl(field) || hasFieldInSelection(selection, field));
}

function getSpecificFields(fields) {
  return fields.filter((field) => field !== SHARED_WHEEL_TIRE_FIELD);
}

function getVisibleTabs(mode) {
  if (mode === 'home') {
    return [TAB.vehicle, TAB.tire];
  }

  const tabs = [TAB.vehicle];

  if (isWheelsPage()) {
    tabs.push(TAB.wheel);
  }

  if (isTiresPage()) {
    tabs.push(TAB.tire);
  }

  return tabs;
}

function getInitialTabKey() {
  if (!currentOptions) {
    return TAB.vehicle.key;
  }

  const selection = getSelection();
  const hasVehicleSelection = hasAnyField(currentOptions.baseFitmentFields, selection);
  const hasWheelSelection = hasAnyField(getSpecificFields(currentOptions.wheelsFields), selection);
  const hasTireSelection = hasAnyField(getSpecificFields(currentOptions.tiresFields), selection);
  const hasSharedDiameterSelection = hasAnyField([SHARED_WHEEL_TIRE_FIELD], selection);
  const wheelsPage = isWheelsPage();
  const tiresPage = isTiresPage();

  if (hasVehicleSelection) {
    return TAB.vehicle.key;
  }

  if (wheelsPage && hasWheelSelection) {
    return TAB.wheel.key;
  }

  if (tiresPage && hasTireSelection) {
    return TAB.tire.key;
  }

  if (hasSharedDiameterSelection) {
    if (wheelsPage) {
      return TAB.wheel.key;
    }

    if (tiresPage) {
      return TAB.tire.key;
    }
  }

  if (isVehicleSelected()) {
    return TAB.vehicle.key;
  }

  if (tiresPage) {
    return TAB.tire.key;
  }

  return TAB.vehicle.key;
}

function getTabsState(mode) {
  return {
    mode,
    rootClassName: mode === 'home' ? 'cm_tabs' : 'cm_tabs cm_tabs__search',
    visibleTabs: getVisibleTabs(mode),
    activeTabKey: mode === 'home' ? TAB.vehicle.key : getInitialTabKey(),
  };
}

function renderRadioInputs(state) {
  return state.visibleTabs
    .map(
      (tab) => `
        <input
          type="radio"
          name="css-tabs-${state.mode}"
          id="cm_tab-${tab.key}"
          ${tab.key === state.activeTabKey ? 'checked' : ''}
          class="tab-switch"
        >
      `,
    )
    .join('');
}

function getTabLabel(tab, mode) {
  if (mode === 'home') {
    if (tab.key === TAB.vehicle.key) {
      return 'Vehicle Search';
    }

    if (tab.key === TAB.tire.key) {
      return 'Tire Search';
    }
  }

  return tab.label;
}

function renderTabHeaders(state) {
  return state.visibleTabs
    .map(
      (tab) => `<label for="cm_tab-${tab.key}" class="cm_tab-label">${getTabLabel(tab, state.mode)}</label>`,
    )
    .join('');
}

function getPanelClassName(tabKey) {
  return [
    'cm_tab-content',
    'cm_tab',
    tabKey === TAB.vehicle.key && 'cm-ymm',
    tabKey === TAB.tire.key && 'cm-tire-tab',
    tabKey === TAB.vehicle.key && 'cm_vehicle-widget__tab',
  ]
    .filter(Boolean)
    .join(' ');
}

function renderTabPanels(state) {
  return state.visibleTabs
    .map((tab) => {
      return `
        <div
          id="${tab.panelId}"
          class="${getPanelClassName(tab.key)}"
          data-tab="${tab.key}"
        >
          <div id="${tab.slotId}"></div>
        </div>
      `;
    })
    .join('');
}

function createTabsMarkup(mode) {
  const state = getTabsState(mode);
  const content = `
    <div class="${state.rootClassName}">
      ${renderRadioInputs(state)}
      <div class="cm_tabs-header">
        ${renderTabHeaders(state)}
      </div>
      <div class="cm_tabs-contents">
        ${renderTabPanels(state)}
      </div>
    </div>
  `;

  return mode === 'home' ? `<div class="container">${content}</div>` : content;
}

function handleSearchTabSwitch(tabKey) {
  if (!currentOptions) {
    return;
  }

  if (isVehicleSelected() && (tabKey === TAB.wheel.key || tabKey === TAB.tire.key)) {
    window.Convermax.discardVehicle?.();
  }

  const wheelResetFields = currentOptions.wheelsFields;
  if (tabKey === TAB.vehicle.key && isWheelsPage()) {
    window.Convermax.discardSearchFields?.(wheelResetFields);
  }

  const tireResetFields = currentOptions.tiresFields;
  if (tabKey === TAB.vehicle.key && isTiresPage()) {
    window.Convermax.discardSearchFields?.(tireResetFields);
  }
}

function bindSearchTabs(root) {
  root.addEventListener('change', (e) => {
    const input = e.target.closest('.tab-switch');
    if (!input) {
      return;
    }

    const tabKey = input.id.replace('cm_tab-', '');
    handleSearchTabSwitch(tabKey);
  });
}

function mountSearchTabs() {
  if (!shouldRenderSearchTabs() || window.document.getElementById(SEARCH_TABS_MOUNT_ID)) {
    return;
  }

  const header = window.document.querySelector('header');
  if (!header) {
    return;
  }

  const mountPoint = window.document.createElement('div');
  mountPoint.id = SEARCH_TABS_MOUNT_ID;
  mountPoint.className = 'cm_vehicle-widget__search-container';
  mountPoint.innerHTML = createTabsMarkup('search');
  mountPoint.dataset.tabsInitialized = '1';

  header.appendChild(mountPoint);
  bindSearchTabs(mountPoint);
}

function mountHomeTabs() {
  const mountPoint = window.document.querySelector(HOME_TABS_MOUNT_SELECTOR);
  if (!mountPoint || mountPoint.dataset.tabsInitialized === '1') {
    return;
  }

  mountPoint.innerHTML = createTabsMarkup('home');
  mountPoint.dataset.tabsInitialized = '1';
}

export function initTabs(options) {
  currentOptions = options;
  mountHomeTabs();
  mountSearchTabs();
}
